export type ClientTelemetryProvider = {
  getClientSessionId: () => string | Promise<string>;
  newCorrelationId: () => string;
  getClientPlatform?: () => string | Promise<string>;
  getClientVersion?: () => string | Promise<string>;
};

export type ClientTelemetryRequestInfo = {
  clientAction?: string;
  method?: string;
  url?: string;
  baseURL?: string;
};

let clientTelemetryProvider: ClientTelemetryProvider | undefined;

const uuidFromCrypto = (): string | undefined => {
  // Avoid depending on DOM lib typings in shared packages.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cryptoObj: any = (globalThis as any)?.crypto;

  if (cryptoObj?.randomUUID) {
    return String(cryptoObj.randomUUID());
  }

  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);

    // Set version (4) and variant (10)
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
      ''
    );
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return undefined;
};

const uuidFallback = (): string => {
  return `cc_${Date.now().toString(16)}_${Math.random().toString(16).slice(2)}`;
};

let fallbackSessionId: string | undefined;
const getFallbackSessionId = (): string => {
  if (!fallbackSessionId) {
    fallbackSessionId = uuidFromCrypto() ?? uuidFallback();
  }
  return fallbackSessionId;
};

export const setClientTelemetryProvider = (provider: ClientTelemetryProvider) => {
  clientTelemetryProvider = provider;
};

const getDefaultClientAction = (info?: ClientTelemetryRequestInfo): string => {
  // Safety: never leak PII via this default.
  // We generate a coarse endpoint signature (no query string, sanitized path segments).
  // Explicit UI actions should be passed by call sites as `clientAction`.
  const method = info?.method?.toUpperCase();
  const pathname = getSafePathname(info);
  const sanitized = pathname ? sanitizePathname(pathname) : undefined;
  if (!method) {
    return sanitized ? `API:UNKNOWN:${sanitized}` : 'API:UNKNOWN';
  }
  return sanitized ? `API:${method}:${sanitized}` : `API:${method}`;
};

const getSafePathname = (info?: ClientTelemetryRequestInfo): string | undefined => {
  const rawUrl = info?.url;
  if (!rawUrl) return undefined;

  try {
    // Axios request.url may be relative; baseURL may be set.
    const base = info?.baseURL ?? 'http://localhost';
    const parsed = new URL(rawUrl, base);
    return parsed.pathname;
  } catch {
    // Best-effort fallback for unusual URL formats.
    const withoutQuery = String(rawUrl).split('?')[0];
    const pathnameOnly = withoutQuery.split('#')[0];
    // If it's a full URL without URL() parsing, strip scheme/host.
    const idx = pathnameOnly.indexOf('://');
    if (idx >= 0) {
      const afterScheme = pathnameOnly.slice(idx + 3);
      const firstSlash = afterScheme.indexOf('/');
      return firstSlash >= 0 ? afterScheme.slice(firstSlash) : '/';
    }
    return pathnameOnly.startsWith('/') ? pathnameOnly : `/${pathnameOnly}`;
  }
};

const sanitizePathname = (pathname: string): string => {
  const clean = pathname.split('?')[0].split('#')[0];
  const segments = clean
    .split('/')
    .filter(Boolean)
    .map((seg) => sanitizeSegment(seg));

  return '/' + segments.join('/');
};

const sanitizeSegment = (segment: string): string => {
  const seg = segment.trim();
  if (!seg) return '';

  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) {
    return ':id';
  }

  // Pure numbers (likely ids)
  if (/^\d+$/.test(seg)) {
    return ':id';
  }

  // Long hex/base64-ish tokens (likely ids, hashes, or opaque tokens)
  if (/^[0-9a-f]{12,}$/i.test(seg)) {
    return ':id';
  }
  if (/^[A-Za-z0-9_-]{20,}$/.test(seg)) {
    return ':id';
  }

  // Emails / user-identifiers
  if (seg.includes('@')) {
    return ':id';
  }

  // Any overly long segment is treated as an id
  if (seg.length > 32) {
    return ':id';
  }

  return seg;
};

export const getClientTelemetryHeaders = async (
  input?: string | ClientTelemetryRequestInfo
) => {
  const headers: Record<string, string> = {};

  const clientAction =
    typeof input === 'string' ? input : (input as ClientTelemetryRequestInfo | undefined)?.clientAction;
  const requestInfo = typeof input === 'string' ? undefined : input;

  const baseAction = getDefaultClientAction(requestInfo);
  const apiOverride = normalizeClientAction(clientAction);

  // Always use the same API-signature format.
  // If a caller explicitly passes an API:... value, prefer that.
  headers['x-client-action'] = apiOverride ?? baseAction;

  if (!clientTelemetryProvider) {
    // Not configured yet (app should call setClientTelemetryProvider early).
    // Still provide best-effort session/correlation ids so headers are always present.
    headers['x-client-session-id'] = getFallbackSessionId();
    headers['x-correlation-id'] = uuidFromCrypto() ?? uuidFallback();
    headers['x-client-platform'] = getFallbackPlatform();
    headers['x-client-version'] = 'unknown';
    return headers;
  }

  const sessionId = await clientTelemetryProvider.getClientSessionId();
  const correlationId = clientTelemetryProvider.newCorrelationId();
  const platform = clientTelemetryProvider.getClientPlatform
    ? await clientTelemetryProvider.getClientPlatform()
    : getFallbackPlatform();
  const version = clientTelemetryProvider.getClientVersion
    ? await clientTelemetryProvider.getClientVersion()
    : undefined;

  headers['x-client-session-id'] = sessionId;
  headers['x-correlation-id'] = correlationId;
  headers['x-client-platform'] = String(platform ?? 'unknown');
  headers['x-client-version'] = String(version ?? 'unknown');

  return headers;
};

const normalizeClientAction = (clientAction?: string): string | undefined => {
  const trimmed = clientAction?.trim();
  if (!trimmed) return undefined;

  // Only allow explicit API override values.
  // UI labels should not change the header format.
  return trimmed.startsWith('API:') ? trimmed : undefined;
};

const getFallbackPlatform = (): string => {
  // Best-effort platform detection if provider isn't set.
  // Prefer being coarse over being wrong.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav: any = (globalThis as any)?.navigator;
    if (nav?.product === 'ReactNative') {
      return 'native';
    }
    if (typeof (globalThis as any)?.document !== 'undefined') {
      return 'web';
    }
  } catch {
    // ignore
  }
  return 'unknown';
};
